import DoneIcon from "@mui/icons-material/Done";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import { Box, ButtonBase, CircularProgress, Tooltip } from "@mui/material";
import { useContactCenterTheme } from "@components/virtual-receptionist/theming/ContactCenterThemeContext";
import { SmsDeliveryStatus } from "../types/SmsMessage";

interface Props {
  status?: SmsDeliveryStatus;
  errorMessage?: string;
  isRetrying: boolean;
  onRetry: () => void;
}

const PENDING_STATUSES: SmsDeliveryStatus[] = ["queued", "sending", "sent"];
const tooltipPopperProps = { popper: { sx: { zIndex: "2147483647 !important" } } };

/** Text shown when hovering anywhere over the message bubble, not just the status icon. */
export const getSmsStatusTooltip = (status?: SmsDeliveryStatus, errorMessage?: string): string => {
  if (!status) return "";
  if (status === "delivered") return "Delivered";
  if (PENDING_STATUSES.includes(status)) return "Pending";
  if (status === "not-delivered") return errorMessage || "Message could not be delivered";
  return "";
};

export function MessageDeliveryStatus({ status, errorMessage, isRetrying, onRetry }: Props) {
  const { theme } = useContactCenterTheme();

  if (!status) return null;

  if (status === "delivered") {
    return (
      <DoneAllIcon sx={{ fontSize: 15, color: theme.chat.dividerText, flexShrink: 0, mb: 0.5 }} />
    );
  }

  if (PENDING_STATUSES.includes(status)) {
    return <DoneIcon sx={{ fontSize: 15, color: theme.chat.dividerText, flexShrink: 0, mb: 0.5 }} />;
  }

  if (status !== "not-delivered") return null;

  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, flexShrink: 0, mb: 0.5 }}>
      <Tooltip title={errorMessage || "Message could not be delivered"} placement="top" slotProps={tooltipPopperProps}>
        <ErrorOutlineIcon sx={{ fontSize: 15, color: "error.main", opacity: 0.7 }} />
      </Tooltip>

      <ButtonBase
        onClick={onRetry}
        disabled={isRetrying}
        aria-label="Resend message"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.25,
          px: 0.25,
          borderRadius: "4px",
          fontSize: "0.72rem",
          fontWeight: 600,
          lineHeight: 1.2,
          color: theme.link.color,
          textDecoration: "none",
          transition: "color 0.15s ease",
          "&:hover": {
            color: theme.link.hoverColor,
            textDecoration: "underline",
          },
          "&.Mui-disabled": { color: theme.link.color, opacity: 0.6 },
        }}
      >
        {isRetrying ? (
          <CircularProgress size={11} sx={{ color: theme.link.color }} />
        ) : (
          <ReplayIcon sx={{ fontSize: 13 }} />
        )}
        Resend
      </ButtonBase>
    </Box>
  );
}
